import type { StatusIconName } from '../../components/ui/StatusIcon';
import type { ReverseAwardReasonCode } from '../../api/types';

/** Longest note the API accepts (rhino-ops-console#230 Contract). */
export const NOTE_MAX = 200;

/**
 * The one code → label mapping for the reverse-award dialog and the bid table.
 * `hint` is the longer wording shown under the label in the dialog only.
 */
export const REVERSE_AWARD_REASONS: readonly {
  code: ReverseAwardReasonCode;
  label: string;
  hint: string;
  icon: StatusIconName;
}[] = [
  {
    code: 'TRUCK_NOT_ARRIVED',
    label: 'No truck',
    hint: 'Truck didn’t arrive',
    icon: 'local_shipping',
  },
  {
    code: 'PARTNER_BACKED_OUT',
    label: 'Partner backed out',
    hint: 'Partner can’t do this load',
    icon: 'undo',
  },
  {
    code: 'SHIPPER_CHANGED_PLANS',
    label: 'Shipper changed',
    hint: 'Shipper changed plans',
    icon: 'event_busy',
  },
  { code: 'OTHER', label: 'Other', hint: 'Add a short note', icon: 'edit' },
];

/** Unknown or newer codes still read as a reason, never as a raw code. */
export function reverseAwardReasonLabel(code: string): string {
  return REVERSE_AWARD_REASONS.find((r) => r.code === code)?.label ?? 'Other reason';
}

/** A reason is picked, the note fits, and Other carries a note. */
export function reverseAwardReasonValid(
  code: ReverseAwardReasonCode | null,
  note: string,
): boolean {
  if (!code) return false;
  const trimmed = note.trim();
  if (trimmed.length > NOTE_MAX) return false;
  return code !== 'OTHER' || trimmed.length > 0;
}

const REASON_ERRORS: Record<string, string> = {
  SHIPMENT_REASON_REQUIRED: 'Pick a reason. For Other, add a short note.',
  SHIPMENT_REASON_INVALID:
    'That reason wasn’t accepted. Pick one of the choices and keep the note to 200 characters or fewer.',
};

/** Copy for the API's reason refusals; null for any other code (the caller keeps its own message). */
export function reverseAwardReasonError(code: string | undefined): string | null {
  const copy = code ? REASON_ERRORS[code] : undefined;
  return typeof copy === 'string' ? copy : null;
}
